/**
 * Load/save entry points for SSL level files: JSON parse → migrate → validate.
 * Migrations run oldest-first; each is a pure function kept forever.
 */
import type { LevelData } from './types';
import { CURRENT_FORMAT_VERSION } from './types';
import { validateLevel, LevelValidationError } from './validate';

type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * migrations[n] upgrades a document from formatVersion n to n+1.
 * v1 is the first version, so the table is empty today.
 */
const MIGRATIONS: Record<number, Migration> = {};

export function migrateLevel(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  let doc = raw as Record<string, unknown>;
  const initial = doc.formatVersion;
  if (typeof initial !== 'number' || !Number.isInteger(initial)) return raw;
  let v: number = initial;
  while (v < CURRENT_FORMAT_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) {
      throw new LevelValidationError([
        `formatVersion: no migration path from v${v} to v${CURRENT_FORMAT_VERSION}`,
      ]);
    }
    doc = step(doc);
    const next = doc.formatVersion;
    if (typeof next !== 'number' || next <= v) {
      throw new LevelValidationError([
        `formatVersion: migration from v${v} did not advance the version`,
      ]);
    }
    v = next;
  }
  return doc;
}

/** Parse a level file's text content into validated LevelData. */
export function parseLevel(text: string): LevelData {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new LevelValidationError([`(root): invalid JSON — ${(e as Error).message}`]);
  }
  return validateLevel(migrateLevel(raw));
}

/** Serialize LevelData to file text (stable field order via replacer not needed for v1). */
export function stringifyLevel(level: LevelData, pretty = false): string {
  return JSON.stringify(level, null, pretty ? 2 : 0);
}
